import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { Eye, Trash2 } from 'lucide-react';
import type { PatientMeasurementRecord } from '../types';
import {
  formatDateTimeValue,
  formatMeasurementField,
} from '../utils/formatters';
import { BASE_MEASUREMENT_FIELD_DEFINITIONS } from '../utils/measurements';
import { getMeasurementNumericValue } from '../utils/measurementHistoryChart';

interface PatientMeasurementsHistoryTableProps {
  history: PatientMeasurementRecord[];
  deletingMeasurementId?: number | null;
  onView?: (measurement: PatientMeasurementRecord) => void;
  onDelete?: (measurement: PatientMeasurementRecord) => void;
}

const formatCompactMeasurementValue = (
  record: PatientMeasurementRecord,
  metricKey: string
) => {
  const numericValue = getMeasurementNumericValue(record, metricKey);
  if (numericValue == null) {
    return '—';
  }

  const definition = BASE_MEASUREMENT_FIELD_DEFINITIONS.find(
    item => item.metricType === metricKey
  );
  const unit = definition?.unit;

  return unit ? `${numericValue.toFixed(1)} ${unit}` : numericValue.toFixed(1);
};

export function PatientMeasurementsHistoryTable({
  history,
  deletingMeasurementId = null,
  onView,
  onDelete,
}: PatientMeasurementsHistoryTableProps) {
  const hasRowActions = onView != null || onDelete != null;
  if (history.length === 0) {
    return (
      <Paper withBorder p='lg' radius='sm'>
        <Text size='sm' c='dimmed'>
          Nessuna rilevazione disponibile.
        </Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder radius='sm' p='sm'>
      <Stack gap='sm'>
        <Box>
          <Text size='sm' fw={600}>
            Storico rilevazioni
          </Text>
          <Text size='xs' c='dimmed'>
            Tabella compatta delle misure registrate nel tempo.
          </Text>
        </Box>

        <ScrollArea type='auto' offsetScrollbars>
          <Table
            striped
            highlightOnHover
            withTableBorder
            horizontalSpacing='xs'
            verticalSpacing={6}
            fz='xs'
          >
            <Table.Thead>
              <Table.Tr bg='var(--mantine-color-gray-0)'>
                <Table.Th w={140}>Data</Table.Th>
                {BASE_MEASUREMENT_FIELD_DEFINITIONS.map(definition => (
                  <Table.Th key={definition.metricType} ta='right'>
                    {definition.label}
                  </Table.Th>
                ))}
                <Table.Th miw={160}>Note</Table.Th>
                {hasRowActions ? <Table.Th w={112}>Azioni</Table.Th> : null}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {history.map((entry, index) => (
                <Table.Tr key={entry.id ?? `${entry.patientId}-${index}`}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text size='xs' fw={500}>
                        {formatDateTimeValue(entry.createdAt)}
                      </Text>
                      {index === 0 ? (
                        <Badge size='xs' variant='light' color='teal'>
                          Ultima
                        </Badge>
                      ) : null}
                    </Stack>
                  </Table.Td>
                  {BASE_MEASUREMENT_FIELD_DEFINITIONS.map(definition => (
                    <Table.Td key={definition.metricType} ta='right'>
                      <Text size='xs' fw={500}>
                        {formatCompactMeasurementValue(
                          entry,
                          definition.metricType
                        )}
                      </Text>
                    </Table.Td>
                  ))}
                  <Table.Td>
                    <Text
                      size='xs'
                      c={entry.notes.trim() ? undefined : 'dimmed'}
                      lineClamp={2}
                    >
                      {entry.notes.trim() || '—'}
                    </Text>
                    {entry.fields.some(field => field.metricType == null) ? (
                      <Text size='xs' c='dimmed' mt={4} lineClamp={2}>
                        {entry.fields
                          .filter(field => field.metricType == null)
                          .map(
                            field =>
                              `${field.label}: ${formatMeasurementField(field)}`
                          )
                          .join(' · ')}
                      </Text>
                    ) : null}
                  </Table.Td>
                  {hasRowActions ? (
                    <Table.Td>
                      <Group gap={4} wrap='nowrap' justify='flex-end'>
                        {onView ? (
                          <Button
                            variant='subtle'
                            size='compact-xs'
                            leftSection={<Eye size={14} />}
                            onClick={() => onView(entry)}
                          >
                            Vedi
                          </Button>
                        ) : null}
                        {onDelete && entry.id != null ? (
                          <Tooltip label='Elimina rilevazione'>
                            <ActionIcon
                              variant='subtle'
                              color='red'
                              size='sm'
                              aria-label='Elimina rilevazione'
                              loading={deletingMeasurementId === entry.id}
                              disabled={
                                deletingMeasurementId != null &&
                                deletingMeasurementId !== entry.id
                              }
                              onClick={() => onDelete(entry)}
                            >
                              <Trash2 size={14} />
                            </ActionIcon>
                          </Tooltip>
                        ) : null}
                      </Group>
                    </Table.Td>
                  ) : null}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Stack>
    </Paper>
  );
}
